/**
 * Valuation projects: their own store, because they are their own thing.
 *
 * A valuation used to be a block inside an accounting model, which made two
 * ordinary things impossible - valuing a company you have no accounts for, and
 * holding two valuations of one set of accounts on different assumptions.
 *
 * A separate store rather than a slice of the model store, for the same reason the
 * backend gives them their own table and their own package: they share no state. A
 * valuation has no inputs, no statements and no balance check. Putting them
 * together would mean a store where half the fields are meaningless depending on
 * what is open.
 *
 * See docs/accounting-valuation-separation.md.
 */

import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';

import { useAuthStore } from './auth';

function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().token;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function jsonHeaders(): Record<string, string> {
  return { 'Content-Type': 'application/json', ...authHeaders() };
}

/** The four ways a valuation gets its figures. */
export type SourceKind = 'linked' | 'form' | 'excel' | 'pnl';

export type Units = 'units' | 'thousands' | 'millions';

/** The six numbers a year that a valuation consumes. Nothing else is read. */
export interface YearFigures {
  ebitda: number;
  capex: number;
  /** A LEVEL at year end, not a movement: the engine differences the years. */
  workingCapital: number;
  taxPaid: number;
  cash: number;
  debt: number;
}

export interface ValuationSource {
  kind: SourceKind;
  modelId?: string | null;
  modelName?: string | null;
  /** Which discipline a linked valuation is pinned to. */
  modelKind?: 'ias' | 'baseline';
  pinnedAt?: string | null;
  filename?: string | null;
  units: Units;
}

export interface ValuationConfig {
  discountRate: number;
  terminalGrowthRate: number;
  exitMultipleEVEBITDA: number;
  sharesOutstanding: number;
  terminalYear: number;
  [key: string]: unknown;
}

export interface ValuationProject {
  id: string;
  name: string;
  description: string;
  source: ValuationSource;
  figures: { years: Record<string, YearFigures>; origin: string };
  config: ValuationConfig;
  computed: Record<string, unknown> | null;
  notes: string[];
  /** What a due diligence project sent: a separate, removable layer (never applied in place). */
  diligenceLayer?: Record<string, unknown> | null;
  /** What the linked filing's text says, as proposed effects on the assumptions. */
  qualitative?: Qualitative | null;
  version: number;
  createdAt: string;
  updatedAt: string;
}

export interface FilingSignal {
  id: string;
  kind: string;
  severity: string;
  title: string;
  page: number | null;
  quote: string;
  proposal: { assumption: string; delta: number; why: string } | null;
}

export interface Qualitative {
  signals: FilingSignal[];
  effects: Array<{ signalId: string; title: string; assumption: string; delta: number; acceptedAt: string }>;
  dismissed: string[];
}

export interface ValuationSample {
  key: string;
  name: string;
  summary: string;
  teaches: string[];
}

export interface ValuationMetadata {
  id: string;
  name: string;
  description: string;
  sourceKind: SourceKind;
  modelId: string | null;
  modelName: string | null;
  modelKind?: 'ias' | 'baseline';
  pinnedAt: string | null;
  years: number[];
  equityValue: number | null;
  notes: string[];
  updatedAt: string;
}

/** One field of one year that has moved since a valuation was pinned. */
export interface PendingChange {
  year: number;
  field: keyof YearFigures;
  was: number | null;
  now: number | null;
}

interface State {
  list: ValuationMetadata[];
  samples: ValuationSample[];
  project: ValuationProject | null;
  /** What has changed in the accounts since this valuation was pinned. */
  changes: PendingChange[] | null;
  sourceMissing: boolean;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
}

interface Actions {
  listValuations: () => Promise<void>;
  listSamples: () => Promise<void>;
  /** The user's own copy of a sample - made the first time, reopened after. Its id. */
  openSample: (key: string) => Promise<string | null>;
  open: (id: string) => Promise<void>;
  close: () => void;
  create: (body: {
    name: string;
    source: Partial<ValuationSource> & { kind: SourceKind };
    figures?: { years: Record<string, YearFigures> };
    pnl?: Record<string, Record<string, number>>;
    /** Assumptions, and the method inputs a spreadsheet supplied. */
    config?: Partial<ValuationConfig>;
  }) => Promise<string | null>;
  update: (
    id: string,
    body: {
      name?: string;
      config?: Partial<ValuationConfig>;
      figures?: { years: Record<string, YearFigures> };
    },
  ) => Promise<void>;
  /** What moved in the accounts since the pin. Shown BEFORE refreshing. */
  loadChanges: (id: string) => Promise<void>;
  refresh: (id: string) => Promise<void>;
  /** The valuation as a workbook - live formulas, the explanation, the figures. */
  exportWorkbook: (id: string, name: string) => Promise<void>;
  unlink: (id: string) => Promise<void>;
  removeDiligenceLayer: (id: string) => Promise<void>;
  /** Accept a filing signal's effect (as proposed, or edited), dismiss it, or undo. */
  decideSignal: (id: string, signalId: string, status: 'accepted' | 'dismissed' | 'open', delta?: number) => Promise<void>;
  remove: (id: string) => Promise<void>;
  /**
   * Read a spreadsheet WITHOUT creating anything.
   *
   * Two steps on purpose: the parser reports which sheet it used, which row held
   * the headings and which columns it could not name, and that report is worth
   * more before a valuation exists than after. A sheet that yields three of five
   * years produces figures that look complete.
   */
  parseWorkbook: (file: File) => Promise<{
    years: Record<string, YearFigures>;
    notes: string[];
    /** The method-input blocks the file held, where it held any. */
    methodInputs: Record<string, unknown> | null;
    methodNotes: string[];
  } | null>;
  dismissError: () => void;
}

const BASE = '/api/v2/valuations';

export const useValuationStore = create<State & Actions>()(
  immer((set, get) => ({
    list: [],
    samples: [],
    project: null,
    changes: null,
    sourceMissing: false,
    isLoading: false,
    isSaving: false,
    error: null,

    dismissError: () =>
      set((state) => {
        state.error = null;
      }),

    close: () =>
      set((state) => {
        state.project = null;
        state.changes = null;
        state.sourceMissing = false;
      }),

    parseWorkbook: async (file) => {
      set((state) => {
        state.isSaving = true;
        state.error = null;
      });
      try {
        const base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const result = String(reader.result);
            // The API wants the bytes, not the data: URL prefix the reader adds.
            resolve(result.slice(result.indexOf(',') + 1));
          };
          reader.onerror = () => reject(new Error('That file could not be read.'));
          reader.readAsDataURL(file);
        });
        const response = await fetch(`${BASE}/parse-workbook`, {
          method: 'POST',
          headers: jsonHeaders(),
          body: JSON.stringify({ filename: file.name, file: base64 }),
        });
        if (!response.ok) {
          const detail = await response.json().catch(() => ({}));
          throw new Error(detail.detail ?? 'That spreadsheet could not be read.');
        }
        const body = await response.json();
        set((state) => {
          state.isSaving = false;
        });
        return {
          years: body.figures?.years ?? {},
          notes: body.notes ?? [],
          // Whatever the second reader recognised: a cap rate, a list of buildings,
          // a policy book. Usually nothing, which is why it is nullable rather than
          // an empty object - "there was nothing here" is not the same as "there was
          // something and it came to nothing".
          methodInputs: body.methodInputs ?? null,
          methodNotes: body.methodNotes ?? [],
        };
      } catch (error) {
        set((state) => {
          state.isSaving = false;
          state.error = error instanceof Error ? error.message : String(error);
        });
        return null;
      }
    },

    listSamples: async () => {
      try {
        const response = await fetch(`${BASE}/samples`, { headers: authHeaders() });
        if (!response.ok) return;
        const samples: ValuationSample[] = await response.json();
        set((state) => {
          state.samples = samples;
        });
      } catch {
        // No samples is no loss: the page still starts real valuations.
      }
    },

    openSample: async (key) => {
      try {
        const response = await fetch(`${BASE}/samples/${encodeURIComponent(key)}`, {
          method: 'POST',
          headers: authHeaders(),
        });
        if (!response.ok) {
          const detail = await response.json().catch(() => ({}));
          throw new Error(detail.detail ?? `Could not open the sample (${response.status})`);
        }
        const project: ValuationProject = await response.json();
        await get().listValuations();
        return project.id;
      } catch (error) {
        set((state) => {
          state.error = error instanceof Error ? error.message : String(error);
        });
        return null;
      }
    },

    listValuations: async () => {
      set((state) => {
        state.isLoading = true;
        state.error = null;
      });
      try {
        const response = await fetch(BASE, { headers: authHeaders() });
        if (!response.ok) throw new Error(`Could not load valuations (${response.status})`);
        const rows: ValuationMetadata[] = await response.json();
        set((state) => {
          state.list = rows;
          state.isLoading = false;
        });
      } catch (error) {
        set((state) => {
          state.isLoading = false;
          state.error = error instanceof Error ? error.message : String(error);
        });
      }
    },

    open: async (id) => {
      set((state) => {
        state.isLoading = true;
        state.error = null;
        // Cleared, so a stale diff from the previously open valuation cannot be
        // read as belonging to this one.
        state.changes = null;
        state.sourceMissing = false;
      });
      try {
        const response = await fetch(`${BASE}/${id}`, { headers: authHeaders() });
        if (!response.ok) throw new Error(`Could not open the valuation (${response.status})`);
        const project: ValuationProject = await response.json();
        set((state) => {
          state.project = project;
          state.isLoading = false;
        });
      } catch (error) {
        set((state) => {
          state.isLoading = false;
          state.error = error instanceof Error ? error.message : String(error);
        });
      }
    },

    create: async (body) => {
      set((state) => {
        state.isSaving = true;
        state.error = null;
      });
      try {
        const response = await fetch(BASE, {
          method: 'POST',
          headers: jsonHeaders(),
          body: JSON.stringify({
            name: body.name,
            source: { units: 'units', ...body.source },
            figures: body.figures,
            pnl: body.pnl,
            config: body.config,
          }),
        });
        if (!response.ok) {
          const detail = await response.json().catch(() => ({}));
          throw new Error(detail.detail ?? `Could not create it (${response.status})`);
        }
        const project: ValuationProject = await response.json();
        set((state) => {
          state.project = project;
          state.isSaving = false;
        });
        await get().listValuations();
        return project.id;
      } catch (error) {
        set((state) => {
          state.isSaving = false;
          state.error = error instanceof Error ? error.message : String(error);
        });
        return null;
      }
    },

    update: async (id, body) => {
      set((state) => {
        state.isSaving = true;
        state.error = null;
      });
      try {
        const response = await fetch(`${BASE}/${id}`, {
          method: 'PATCH',
          headers: jsonHeaders(),
          body: JSON.stringify(body),
        });
        if (!response.ok) {
          const detail = await response.json().catch(() => ({}));
          // The API refuses to edit a linked valuation's figures, and says why.
          // Surfaced rather than swallowed: it is a real answer, not a fault.
          throw new Error(detail.detail ?? `Could not save it (${response.status})`);
        }
        const project: ValuationProject = await response.json();
        set((state) => {
          state.project = project;
          state.isSaving = false;
        });
      } catch (error) {
        set((state) => {
          state.isSaving = false;
          state.error = error instanceof Error ? error.message : String(error);
        });
      }
    },

    loadChanges: async (id) => {
      try {
        const response = await fetch(`${BASE}/${id}/changes`, {
          headers: authHeaders(),
        });
        if (!response.ok) return;
        const body = await response.json();
        set((state) => {
          state.changes = body.changes ?? [];
          state.sourceMissing = Boolean(body.sourceMissing);
        });
      } catch {
        // A diff that cannot be fetched is not worth an error banner: the
        // valuation itself is fine and stands on its pinned figures.
      }
    },

    exportWorkbook: async (id, name) => {
      try {
        const response = await fetch(`${BASE}/${id}/export`, { headers: authHeaders() });
        if (!response.ok) throw new Error(`Export failed (${response.status})`);
        const url = URL.createObjectURL(await response.blob());
        const link = document.createElement('a');
        link.href = url;
        link.download = `${name} - valuation.xlsx`;
        link.click();
        URL.revokeObjectURL(url);
      } catch (error) {
        set({ error: error instanceof Error ? error.message : String(error) });
      }
    },

    refresh: async (id) => {
      set((state) => {
        state.isSaving = true;
      });
      try {
        const response = await fetch(`${BASE}/${id}/refresh`, {
          method: 'POST',
          headers: jsonHeaders(),
        });
        if (!response.ok) {
          const detail = await response.json().catch(() => ({}));
          throw new Error(detail.detail ?? 'Could not refresh it');
        }
        const project: ValuationProject = await response.json();
        set((state) => {
          state.project = project;
          state.changes = [];
          state.isSaving = false;
        });
      } catch (error) {
        set((state) => {
          state.isSaving = false;
          state.error = error instanceof Error ? error.message : String(error);
        });
      }
    },

    unlink: async (id) => {
      const response = await fetch(`${BASE}/${id}/unlink`, {
        method: 'POST',
        headers: jsonHeaders(),
      });
      if (!response.ok) return;
      const project: ValuationProject = await response.json();
      set((state) => {
        state.project = project;
        state.changes = null;
      });
    },

    decideSignal: async (id, signalId, status, delta) => {
      const response = await fetch(`${BASE}/${id}/signals/${encodeURIComponent(signalId)}`, {
        method: 'POST',
        headers: jsonHeaders(),
        body: JSON.stringify({ status, delta }),
      });
      if (!response.ok) {
        const detail = await response.json().catch(() => ({}));
        set((state) => {
          state.error = detail.detail ?? `Could not record that (${response.status})`;
        });
        return;
      }
      const project: ValuationProject = await response.json();
      set((state) => {
        state.project = project;
      });
    },

    removeDiligenceLayer: async (id) => {
      const response = await fetch(`${BASE}/${id}/diligence-layer`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (!response.ok) return;
      const project: ValuationProject = await response.json();
      set((state) => {
        state.project = project;
      });
    },

    remove: async (id) => {
      const response = await fetch(`${BASE}/${id}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (!response.ok) return;
      set((state) => {
        if (state.project?.id === id) state.project = null;
      });
      await get().listValuations();
    },
  })),
);
